import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../../../../core/services/api.service';
import { API_ROUTES } from '../../../../../core/constants/api-routes';
import { BackButton } from '../../../../../shared/components/back-button/back-button';
import {
  PromptOverviewData,
  PromptOverviewRequest,
  PromptHistoryItem,
  PromptHistoryMember,
  PromptOverviewKpi,
  PromptOverviewConfiguration,
} from '../../../../../core/models/schedule-prompt.models';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-promt-detail-view',
  imports: [NgClass, BackButton],
  templateUrl: './prompt-detail-view.html',
  styleUrl: './prompt-detail-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PromptDetailView implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);

  // -- Route param -----------------------------------------------------------
  readonly schedulePromptId = signal('');

  // -- UI state --------------------------------------------------------------
  readonly isInitializing = signal(true);
  readonly hasError = signal(false);
  readonly isLoadingMore = signal(false);
  readonly hasMoreHistory = signal(false);

  // -- Data ------------------------------------------------------------------
  readonly promptTitle = signal('');
  readonly categoryName = signal('');
  readonly stats = signal<PromptOverviewKpi>({
    totalSent: 0,
    responded: 0,
    pending: 0,
    respondedPercent: 0,
    pendingPercent: 0,
    storiesCreated: 0,
  });
  readonly configuration = signal<PromptOverviewConfiguration | null>(null);
  readonly history = signal<PromptHistoryItem[]>([]);
  readonly totalRecords = signal(0);

  // -- Filters ---------------------------------------------------------------
  readonly activeFilter = signal<string | null>(null); // null = All
  readonly pageNumber = signal(1);

  // -- Infinite scroll -------------------------------------------------------
  private scrollObserver?: IntersectionObserver;
  private isRequestInFlight = false;

  // -- Lifecycle -------------------------------------------------------------

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('schedulePromptId') ?? '';
    this.schedulePromptId.set(id);
    this.loadOverview();
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
  }

  // -- API call --------------------------------------------------------------

  async loadOverview(isLoadMore = false): Promise<void> {
    if (this.isRequestInFlight) return;
    if (isLoadMore && !this.hasMoreHistory()) return;

    this.isRequestInFlight = true;

    if (isLoadMore) {
      this.isLoadingMore.set(true);
    } else {
      this.hasError.set(false);
    }

    const request: PromptOverviewRequest = {
      schedulePromptId: this.schedulePromptId(),
      historyFilter: this.activeFilter(),
      page: this.pageNumber(),
      pageSize: PAGE_SIZE,
    };

    try {
      const resp = await this.api.request<PromptOverviewData>(
        'POST',
        API_ROUTES.SP_GET_DETAIL_PROMPT_OVERVIEW,
        request,
        { showToaster: false, showLoader: !isLoadMore, useToken: true },
      );

      if (resp.status && resp.data) {
        const data = resp.data;

        if (!isLoadMore) {
          this.promptTitle.set(data.promptTitle);
          this.categoryName.set(data.categoryName);
          this.stats.set(data.kpi);
          this.configuration.set(data.configuration);
        }

        const incoming = Array.isArray(data.promptHistory) ? data.promptHistory : [];
        const total = data.totalHistoryRecords ?? 0;

        if (isLoadMore) {
          this.history.update((existing) => [...existing, ...incoming]);
        } else {
          this.history.set(incoming);
        }

        this.totalRecords.set(total);
        this.hasMoreHistory.set(this.history().length < total);
      } else {
        if (!isLoadMore) {
          this.hasError.set(true);
          this.history.set([]);
        }
        this.hasMoreHistory.set(false);
      }
    } catch {
      if (!isLoadMore) {
        this.hasError.set(true);
        this.history.set([]);
      }
      this.hasMoreHistory.set(false);
    } finally {
      this.isLoadingMore.set(false);
      this.isRequestInFlight = false;
      this.isInitializing.set(false);
      this.cdr.markForCheck();

      if (!isLoadMore && isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.setupScrollObserver(), 100);
      }
    }
  }
  getLatestResponseDate(members: PromptHistoryMember[]): string | null {
    const dates = members
      .filter((m) => m.responseStatus?.toLowerCase() === 'responded' && m.responseDate)
      .map((m) => m.responseDate as string);

    if (!dates.length) return null;

    return dates.reduce((latest, d) => (new Date(d) > new Date(latest) ? d : latest));
  }
  // -- Infinite scroll -------------------------------------------------------

  private setupScrollObserver(): void {
    this.scrollObserver?.disconnect();
    const sentinel = document.querySelector('#historyScrollSentinel');
    if (!sentinel) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isRequestInFlight && this.hasMoreHistory()) {
          this.zone.run(() => {
            this.pageNumber.update((n) => n + 1);
            this.loadOverview(true);
          });
        }
      },
      { threshold: 0.1 },
    );

    this.scrollObserver.observe(sentinel);
  }

  // -- Filter ----------------------------------------------------------------

  onFilterChange(filter: string | null): void {
    this.activeFilter.set(filter);
    this.pageNumber.set(1);
    this.loadOverview();
  }

  // -- Helpers ---------------------------------------------------------------

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /** Aggregates per-member statuses into a single status for the timeline dot. */
  getOverallStatus(members: PromptHistoryMember[]): string {
    if (!members?.length) return '';
    const statuses = members.map((m) => m.responseStatus?.toLowerCase());
    if (statuses.every((s) => s === 'responded')) return 'Responded';
    if (statuses.some((s) => s === 'waiting')) return 'Waiting';
    return 'NotResponded';
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'fm-responded';
      case 'notresponded':
        return 'fm-not-responded';
      case 'waiting':
        return 'fm-waiting';
      default:
        return '';
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'fm-status-success';
      case 'notresponded':
        return 'fm-status-danger';
      case 'waiting':
        return 'fm-status-warning';
      default:
        return '';
    }
  }

  getStatusIcon(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'bi-check-lg';
      case 'notresponded':
        return 'bi-x-lg';
      case 'waiting':
        return 'bi-hourglass-split';
      default:
        return '';
    }
  }

  getStatusBadgeIcon(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'bi-camera-video-fill';
      case 'notresponded':
        return 'bi-camera-video-fill';
      case 'waiting':
        return 'bi-hourglass-split';
      default:
        return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status?.toLowerCase()) {
      case 'responded':
        return 'Responded';
      case 'notresponded':
        return 'Not Responded';
      case 'waiting':
        return 'Waiting Response';
      default:
        return status;
    }
  }
  getAvatarClass(status: string): string {
  switch (status?.toLowerCase()) {
    case 'responded':    return 'fm-avatar-responded';
    case 'notresponded': return 'fm-avatar-not-responded';
    case 'waiting':      return 'fm-avatar-waiting';
    default:             return '';
  }
}

  getMemberInitials(name: string): string {
    if (!name?.trim()) return '?';
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  getAccordionId(index: number): string {
    return `historyPrompt_${index}`;
  }
}
